/**
 * HierarchicalFinanceDashboard - Finance dashboard with organizational hierarchy support
 * Features:
 * - View finance data based on user's access level
 * - Drill-down by organization unit
 * - Approval workflows
 * - Inter-unit transfers
 * - Privacy controls for donor information
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import CurrencySelect from './CurrencySelect';
import { getToken } from '@/lib/authToken';
import {
  DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Building2, Users, PiggyBank, Receipt, CheckCircle, XCircle,
  Clock, Filter, Download, Plus, ArrowLeftRight, Eye, EyeOff,
  Loader2, ChevronDown, ChevronRight, BarChart3, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons and colors
const INCOME_CATEGORIES = {
  tithe: { label: 'Tithes', color: 'bg-blue-500' },
  offering: { label: 'Offerings', color: 'bg-green-500' },
  donation: { label: 'Donations', color: 'bg-purple-500' },
  fundraiser: { label: 'Fundraiser', color: 'bg-yellow-500' },
  grant: { label: 'Grants', color: 'bg-orange-500' },
  other: { label: 'Other', color: 'bg-gray-500' },
};

const EXPENSE_CATEGORIES = {
  utilities: { label: 'Utilities', color: 'bg-blue-500' },
  salaries: { label: 'Salaries', color: 'bg-green-500' },
  maintenance: { label: 'Maintenance', color: 'bg-yellow-500' },
  supplies: { label: 'Supplies', color: 'bg-purple-500' },
  events: { label: 'Events', color: 'bg-orange-500' },
  missions: { label: 'Missions', color: 'bg-pink-500' },
  other: { label: 'Other', color: 'bg-gray-500' },
};

const HierarchicalFinanceDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [period, setPeriod] = useState('month');
  const [showIncomeDialog, setShowIncomeDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const queryClient = useQueryClient();

  // Platform / assembly home currency — default for money entry
  const { data: currencyList } = useQuery({
    queryKey: ['currency-default'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/currency/list`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error('Failed to fetch currencies');
      return res.json();
    },
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const homeCurrency = (currencyList?.default_currency || 'USD').toUpperCase();

  // Fetch user's permissions
  const { data: myPermissions } = useQuery({
    queryKey: ['my-permissions'],
    queryFn: async () => {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/rbac/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    }
  });

  // Fetch organization units
  const { data: orgUnits } = useQuery({
    queryKey: ['org-units'],
    queryFn: async () => {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/org/units`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    }
  });

  // Fetch dashboard data
  const { data: dashboard, isLoading: dashboardLoading } = useQuery({
    queryKey: ['finance-dashboard', selectedUnit, period],
    queryFn: async () => {
      const token = getToken();
      const params = new URLSearchParams({ period });
      if (selectedUnit) params.append('unit_id', selectedUnit);
      const res = await fetch(`${API_URL}/api/admin/finance/dashboard?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    }
  });

  // Fetch breakdown by unit
  const { data: unitBreakdown } = useQuery({
    queryKey: ['finance-by-unit', selectedUnit, period],
    queryFn: async () => {
      const token = getToken();
      const params = new URLSearchParams({ period });
      if (selectedUnit) params.append('unit_id', selectedUnit);
      const res = await fetch(`${API_URL}/api/admin/finance/dashboard/by-unit?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    },
    enabled: !!orgUnits?.length
  });

  // Fetch pending approvals
  const { data: pendingApprovals } = useQuery({
    queryKey: ['pending-approvals', selectedUnit],
    queryFn: async () => {
      const token = getToken();
      const params = selectedUnit ? `?unit_id=${selectedUnit}` : '';
      const res = await fetch(`${API_URL}/api/admin/finance/approvals${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    }
  });

  // Fetch giving summary
  const { data: givingSummary } = useQuery({
    queryKey: ['giving-summary', selectedUnit, period],
    queryFn: async () => {
      const token = getToken();
      const params = new URLSearchParams({ period });
      if (selectedUnit) params.append('unit_id', selectedUnit);
      const res = await fetch(`${API_URL}/api/admin/finance/giving/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    }
  });

  // Approve/Reject expense mutation
  const approvalMutation = useMutation({
    mutationFn: async ({ expenseId, action, notes }) => {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/finance/expenses/${expenseId}/approve`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action, notes })
      });
      return res.json();
    },
    onSuccess: (data) => {
      toast.success(`Expense ${data.status}`);
      queryClient.invalidateQueries(['pending-approvals']);
      queryClient.invalidateQueries(['finance-dashboard']);
    },
    onError: () => {
      toast.error('Failed to process approval');
    }
  });

  const canApprove = myPermissions?.role?.permissions?.approvals?.includes('approve');
  const canCreate = myPermissions?.role?.permissions?.finance?.includes('create');

  if (dashboardLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Finance Dashboard</h1>
          <p className="text-slate-500">
            {myPermissions?.can_see_all_levels 
              ? 'Viewing all organization units'
              : `Viewing ${myPermissions?.org_unit?.name || 'your assigned units'}`}
          </p>
        </div>
        <div className="flex gap-2">
          {/* Unit Filter */}
          {orgUnits?.length > 1 && (
            <Select value={selectedUnit || 'all'} onValueChange={(v) => setSelectedUnit(v === 'all' ? null : v)}>
              <SelectTrigger className="w-full max-w-[200px]" data-testid="unit-filter">
                <SelectValue placeholder="All Units" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Units</SelectItem>
                {orgUnits?.map((unit) => (
                  <SelectItem key={unit.id} value={unit.id}>
                    {unit.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Period Filter */}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-full max-w-[120px]" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          {canCreate && (
            <>
              <Button onClick={() => setShowIncomeDialog(true)} data-testid="add-income-btn">
                <Plus className="h-4 w-4 mr-2" />
                Income
              </Button>
              <Button variant="outline" onClick={() => setShowExpenseDialog(true)} data-testid="add-expense-btn">
                <Plus className="h-4 w-4 mr-2" />
                Expense
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Income</p>
                <p className="text-2xl font-bold text-green-600">
                  ${dashboard?.summary?.total_income?.toLocaleString() || 0}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Expenses</p>
                <p className="text-2xl font-bold text-red-600">
                  ${dashboard?.summary?.total_expenses?.toLocaleString() || 0}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Net Balance</p>
                <p className={`text-2xl font-bold ${(dashboard?.summary?.net || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  ${dashboard?.summary?.net?.toLocaleString() || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Approvals</p>
                <p className="text-2xl font-bold text-orange-600">
                  {dashboard?.pending_approvals || 0}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="giving">Giving</TabsTrigger>
          <TabsTrigger value="approvals">
            Approvals
            {(pendingApprovals?.pending_count || 0) > 0 && (
              <Badge className="ml-2 bg-red-500">{pendingApprovals.pending_count}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="breakdown">By Unit</TabsTrigger>
          <TabsTrigger value="transfers">Transfers</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Income by Category */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowUpRight className="h-5 w-5 text-green-600" />
                  Income by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.income_by_category?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.income_by_category.map((item) => {
                      const cat = INCOME_CATEGORIES[item.category] || INCOME_CATEGORIES.other;
                      const total = dashboard.summary.total_income || 1;
                      const percent = (item.total / total) * 100;
                      return (
                        <div key={item.category}>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{cat.label}</span>
                            <span className="font-medium">${item.total.toLocaleString()}</span>
                          </div>
                          <Progress value={percent} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No income recorded this period</p>
                )}
              </CardContent>
            </Card>

            {/* Expenses by Category */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowDownRight className="h-5 w-5 text-red-600" />
                  Expenses by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.expenses_by_category?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.expenses_by_category.map((item) => {
                      const cat = EXPENSE_CATEGORIES[item.category] || EXPENSE_CATEGORIES.other;
                      const total = dashboard.summary.total_expenses || 1;
                      const percent = (item.total / total) * 100;
                      return (
                        <div key={item.category}>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{cat.label}</span>
                            <span className="font-medium">${item.total.toLocaleString()}</span>
                          </div>
                          <Progress value={percent} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No expenses recorded this period</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Units Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Organization Units ({dashboard?.unit_count || 0})
              </CardTitle>
              <CardDescription>Financial summary across your accessible units</CardDescription>
            </CardHeader>
          </Card>
        </TabsContent>

        {/* Giving Tab */}
        <TabsContent value="giving" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-slate-500">Tithes</p>
                  <p className="text-3xl font-bold text-blue-600">
                    ${givingSummary?.tithes?.total?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {givingSummary?.tithes?.count || 0} contributions
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-slate-500">Offerings</p>
                  <p className="text-3xl font-bold text-green-600">
                    ${givingSummary?.offerings?.total?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {givingSummary?.offerings?.count || 0} contributions
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-slate-500">Donations</p>
                  <p className="text-3xl font-bold text-purple-600">
                    ${givingSummary?.donations?.total?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {givingSummary?.donations?.count || 0} contributions
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Total Giving This {period === 'week' ? 'Week' : period === 'month' ? 'Month' : period === 'quarter' ? 'Quarter' : 'Year'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-center py-8">
                ${givingSummary?.grand_total?.toLocaleString() || 0}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approvals Tab */}
        <TabsContent value="approvals">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-600" />
                Pending Approvals
              </CardTitle>
              <CardDescription>Expenses awaiting your approval</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingApprovals?.entries?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingApprovals.entries.map((expense) => (
                      <TableRow key={expense.id}>
                        <TableCell className="font-medium">{expense.description}</TableCell>
                        <TableCell>{expense.org_unit?.name || 'Unknown'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{expense.category}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${expense.amount?.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(expense.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {canApprove && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => approvalMutation.mutate({ 
                                  expenseId: expense.id, 
                                  action: 'approve' 
                                })}
                                disabled={approvalMutation.isPending}
                                data-testid={`approve-${expense.id}`}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => approvalMutation.mutate({ 
                                  expenseId: expense.id, 
                                  action: 'reject' 
                                })}
                                disabled={approvalMutation.isPending}
                                data-testid={`reject-${expense.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p>No pending approvals</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Breakdown by Unit Tab */}
        <TabsContent value="breakdown">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Finance by Organization Unit
              </CardTitle>
              <CardDescription>
                {unitBreakdown?.parent_unit?.name || 'All units'} breakdown
              </CardDescription>
            </CardHeader>
            <CardContent>
              {unitBreakdown?.children?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unit</TableHead>
                      <TableHead>Level</TableHead>
                      <TableHead className="text-right">Income</TableHead>
                      <TableHead className="text-right">Expenses</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                      <TableHead>Sub-units</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unitBreakdown.children.map((item) => (
                      <TableRow 
                        key={item.unit.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => setSelectedUnit(item.unit.id)}
                      >
                        <TableCell className="font-medium">{item.unit.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.unit.level}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          ${item.income?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          ${item.expenses?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${item.net >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                          ${item.net?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell>
                          {item.child_count > 0 && (
                            <Badge variant="secondary">{item.child_count}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No sub-units found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transfers Tab */}
        <TabsContent value="transfers">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowLeftRight className="h-5 w-5" />
                    Inter-Unit Transfers
                  </CardTitle>
                  <CardDescription>Track remittances and support between units</CardDescription>
                </div>
                {canCreate && (
                  <Button onClick={() => setShowTransferDialog(true)} data-testid="new-transfer-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Transfer
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-slate-500">Transfers In</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${dashboard?.summary?.transfers_in?.toLocaleString() || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-slate-500">Transfers Out</p>
                    <p className="text-2xl font-bold text-blue-600">
                      ${dashboard?.summary?.transfers_out?.toLocaleString() || 0}
                    </p>
                  </CardContent>
                </Card>
              </div>
              <p className="text-slate-500 text-center py-4">Transfer history will appear here</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Income Dialog */}
      <Dialog open={showIncomeDialog} onOpenChange={setShowIncomeDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Income</DialogTitle>
            <DialogDescription>Add a new income entry</DialogDescription>
          </DialogHeader>
          <IncomeForm 
            key={`income-${homeCurrency}`}
            homeCurrency={homeCurrency}
            orgUnits={orgUnits} 
            onSuccess={() => {
              setShowIncomeDialog(false);
              queryClient.invalidateQueries(['finance-dashboard']);
              queryClient.invalidateQueries(['giving-summary']);
            }} 
          />
        </DialogContent>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Expense</DialogTitle>
            <DialogDescription>Add a new expense entry</DialogDescription>
          </DialogHeader>
          <ExpenseForm 
            key={`expense-${homeCurrency}`}
            homeCurrency={homeCurrency}
            orgUnits={orgUnits}
            onSuccess={() => {
              setShowExpenseDialog(false);
              queryClient.invalidateQueries(['finance-dashboard']);
              queryClient.invalidateQueries(['pending-approvals']);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Income Form Component
const IncomeForm = ({ orgUnits, onSuccess, homeCurrency = 'USD' }) => {
  const [formData, setFormData] = useState({
    org_unit_id: '',
    category: 'offering',
    amount: '',
    currency: homeCurrency,
    description: '',
    donor_name: '',
    payment_method: 'cash',
    is_anonymous: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = getToken();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/finance/income`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
        })
      });
      if (res.ok) {
        toast.success('Income recorded');
        onSuccess();
      } else {
        toast.error('Failed to record income');
      }
    } catch {
      toast.error('Error recording income');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Organization Unit</Label>
        <Select value={formData.org_unit_id} onValueChange={(v) => setFormData({...formData, org_unit_id: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Select unit" />
          </SelectTrigger>
          <SelectContent>
            {orgUnits?.map((unit) => (
              <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Category</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tithe">Tithe</SelectItem>
              <SelectItem value="offering">Offering</SelectItem>
              <SelectItem value="donation">Donation</SelectItem>
              <SelectItem value="fundraiser">Fundraiser</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Amount</Label>
          <Input 
            type="number" 
            value={formData.amount} 
            onChange={(e) => setFormData({...formData, amount: e.target.value})}
            placeholder="0.00"
            required
          />
        </div>
      </div>
      <div>
        <Label>Currency</Label>
        <CurrencySelect
          value={formData.currency}
          onChange={(v) => setFormData({...formData, currency: v})}
          homeCurrency={homeCurrency}
          testId="income-currency"
        />
      </div>
      <div>
        <Label>Donor Name (optional)</Label>
        <Input 
          value={formData.donor_name} 
          onChange={(e) => setFormData({...formData, donor_name: e.target.value})}
          placeholder="Anonymous if blank"
        />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea 
          value={formData.description} 
          onChange={(e) => setFormData({...formData, description: e.target.value})}
        />
      </div>
      <DialogFooter>
        <Button type="submit" disabled={isSubmitting || !formData.org_unit_id || !formData.amount}>
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Record Income
        </Button>
      </DialogFooter>
    </form>
  );
};

// Expense Form Component
const ExpenseForm = ({ orgUnits, onSuccess, homeCurrency = 'USD' }) => {
  const [formData, setFormData] = useState({
    org_unit_id: '',
    category: 'supplies',
    amount: '',
    currency: homeCurrency,
    description: '',
    vendor: '',
    requires_approval: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = getToken();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/finance/expenses`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
        })
      });
      if (res.ok) {
        toast.success('Expense recorded');
        onSuccess();
      } else {
        toast.error('Failed to record expense');
      }
    } catch {
      toast.error('Error recording expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Organization Unit</Label>
        <Select value={formData.org_unit_id} onValueChange={(v) => setFormData({...formData, org_unit_id: v})}>
          <SelectTrigger>
            <SelectValue placeholder="Select unit" />
          </SelectTrigger>
          <SelectContent>
            {orgUnits?.map((unit) => (
              <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Category</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="utilities">Utilities</SelectItem>
              <SelectItem value="salaries">Salaries</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
              <SelectItem value="supplies">Supplies</SelectItem>
              <SelectItem value="events">Events</SelectItem>
              <SelectItem value="missions">Missions</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Amount</Label>
          <Input 
            type="number" 
            value={formData.amount} 
            onChange={(e) => setFormData({...formData, amount: e.target.value})}
            placeholder="0.00"
            required
          />
        </div>
      </div>
      <div>
        <Label>Currency</Label>
        <CurrencySelect
          value={formData.currency}
          onChange={(v) => setFormData({...formData, currency: v})}
          homeCurrency={homeCurrency}
          testId="expense-currency"
        />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea 
          value={formData.description} 
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          required
        />
      </div>
      <div>
        <Label>Vendor</Label>
        <Input 
          value={formData.vendor} 
          onChange={(e) => setFormData({...formData, vendor: e.target.value})}
        />
      </div>
      <DialogFooter>
        <Button type="submit" disabled={isSubmitting || !formData.org_unit_id || !formData.amount || !formData.description}>
          {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Record Expense
        </Button>
      </DialogFooter>
    </form>
  );
};

export default HierarchicalFinanceDashboard;
